/* ==========================================================================
   BLISS — offer add-to-cart, progressive enhancement.

   The form works with zero JavaScript: it is a real POST to /cart/add and
   falls through to the cart page. This file only upgrades that to an
   in-place add + cart drawer when the drawer is actually available.

   That guard matters on this theme. Cart JS is not in theme assets — it
   loads from https://js.shrinetheme.com/js/v2/main.js with `defer`, so
   <cart-drawer> upgrades only after the script arrives. Calling
   renderContents before then silently no-ops and the shopper sees nothing
   happen. So: wait for the custom element, give up after a timeout, and let
   the native form submission take over instead of failing quietly.
   ========================================================================== */

(function () {
  'use strict';

  var DRAWER_WAIT_MS = 3000;

  function whenDrawerReady() {
    if (!window.customElements || !customElements.whenDefined) {
      return Promise.reject(new Error('no custom elements'));
    }
    return Promise.race([
      customElements.whenDefined('cart-drawer'),
      new Promise(function (_, reject) {
        setTimeout(function () {
          reject(new Error('cart-drawer timed out'));
        }, DRAWER_WAIT_MS);
      })
    ]);
  }

  function sectionsToRender() {
    var ids = [];
    if (document.querySelector('cart-drawer')) ids.push('cart-drawer');
    if (document.getElementById('cart-icon-bubble')) ids.push('cart-icon-bubble');
    return ids;
  }

  function setBusy(button, busy) {
    if (!button) return;
    button.toggleAttribute('aria-busy', busy);
    button.disabled = busy;
  }

  function showError(form, message) {
    var box = form.querySelector('[data-bliss-atc-error]');
    if (!box) return;
    box.textContent = message;
    box.hidden = !message;
  }

  function handle(form) {
    if (form.dataset.blissAtcBound) return;
    form.dataset.blissAtcBound = '1';
    form.addEventListener('submit', function (event) {
      // Only intercept when we can plausibly do better than a page load.
      if (!document.querySelector('cart-drawer')) return;

      event.preventDefault();

      var button = form.querySelector('[type="submit"]');
      var idField = form.querySelector('input[name="id"]');
      var qtyField = form.querySelector('input[name="quantity"]');
      if (!idField || !idField.value) {
        form.submit();
        return;
      }

      setBusy(button, true);
      showError(form, '');

      var sections = sectionsToRender();
      var payload = {
        items: [{ id: Number(idField.value), quantity: Number((qtyField && qtyField.value) || 1) }]
      };
      if (sections.length) {
        payload.sections = sections.join(',');
        payload.sections_url = window.location.pathname;
      }

      fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root + 'cart/add.js'
        : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) throw new Error(data.description || data.message || 'Could not add to cart.');
            return data;
          });
        })
        .then(function (data) {
          return whenDrawerReady()
            .then(function () {
              var drawer = document.querySelector('cart-drawer');
              if (drawer && typeof drawer.renderContents === 'function') {
                drawer.renderContents(data);
                // renderContents updates #CartDrawer-CartItems innerHTML but does not
                // clear the is-empty class Liquid sets on the initial empty-cart page
                // load. Native Shrine ATC removes it in its own finally block; we
                // replicate that here so the cart body is not hidden by CSS.
                drawer.classList.remove('is-empty');
              } else {
                window.location.href = '/cart';
              }
            })
            .catch(function () {
              // Drawer never upgraded. The item IS in the cart — go look at it.
              window.location.href = '/cart';
            });
        })
        .catch(function (error) {
          showError(form, error.message || 'Could not add to cart.');
        })
        .finally(function () {
          setBusy(button, false);
        });
    });
  }

  function init(root) {
    (root || document).querySelectorAll('form[data-bliss-atc]').forEach(handle);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
